import dispatchFromParent from '@/Core/store/modules/utils/dispatch-from-parent';

export const DEFAULT_LIMIT = 10;
export const MAX_LIMIT = 10;
export const DEFAULT_PAGE = 1;
export const DEFAULT_SORT_DIRECTION = -1;

/**
 * Some columns represent two or more fields and must be sorted on
 * those fields rather than their label.
 *
 * e.g. owner_name must be sorted on owner_first_name and owner_last_name
 */
function hasSpecialSort(sortField, specialSortColumns) {
  return specialSortColumns && Object.keys(specialSortColumns).includes(sortField);
}

/**
 * Construct a special sort query when multiple columns are involved.
 *
 * This is merely the last-minute construction of the query. This does not
 * handle changing the sort direction, e.g. when a column is clicked twice.
 *
 * Constructs a sort object like so:
 * {
 *   $sort: {
 *     owner_first_name: -1,
 *     owner_last_name: -1,
 *   }
 * }
 *
 * @param {string} sortField like 'owner_name'
 * @param {object} specialSortColumns like...
 * {
 *   owner_name: ['owner_first_name', 'owner_last_name']
 * }
 */
function specialSortParams(sortField, specialSortColumns, sortDirection) {
  const sortColumns = specialSortColumns[sortField];
  if (!sortColumns || sortColumns.length === 0) return {};

  const sort = { $sort: {} };

  sortColumns.forEach(column => {
    sort.$sort[column] = sortDirection;
  });

  return sort;
}

export const paginationDefaultState = {
  /**
   * See the templates store for how to populate this
   * state field from the parent module.
   * e.g. generatePaginationModule({ findAction: 'templates/findTemplates'})
   */
  findAction: null,
  limit: DEFAULT_LIMIT,
  total: null,
  page: DEFAULT_PAGE,
  sortField: 'created_at',
  sortDirection: -1,
  specialSortColumns: null,
};

const paginationModule = {
  namespaced: true,
  // state - Must be set by parent module, see contacts-table or templates
  getters: {
    /**
     * Returns the number of pages:
     * Math.ceil(0/5) = 0
     * Math.ceil(1/5) = 1
     * Math.ceil(5/5) = 1
     * Math.ceil(6/5) = 2, etc...
     */
    numberOfPages: state => Math.ceil(state.total / state.limit),

    /** */
    isOnLastPage: (state, getters) => state.page === getters.numberOfPages,

    /**
     * true if the total is greater than the limit
     */
    hasMoreThanOnePage: state => state.total > state.limit,

    /**
     * Assemble only the sort
     */
    sortQueryParams(state) {
      if (hasSpecialSort(state.sortField, state.specialSortColumns)) {
        return specialSortParams(state.sortField, state.specialSortColumns, state.sortDirection);
      }

      return { $sort: { [state.sortField]: state.sortDirection } };
    },

    /**
     * Assemble all the query params in this module
     */
    queryParams: (state, getters) => ({
      $limit: state.limit,
      $skip: (state.page - 1) * state.limit,
      ...getters.sortQueryParams,
    }),
  },
  mutations: {
    setPage: (state, page) => {
      state.page = page;
    },
    setTotal: (state, total) => {
      state.total = total;
    },
    setLimit: (state, limit) => {
      state.limit = limit;
    },
    setSortField: (state, sortField) => {
      if (sortField === state.sortField) {
        state.sortDirection *= -1; // reverse sort oder
      } else {
        state.sortDirection = -1;
      }
      state.sortField = sortField;
    },
    setSortDirection: (state, sortDirection) => {
      state.sortDirection = sortDirection;
    },
  },

  /**
   * Most actions follow a similar pattern:
   *   - set a value.
   *   - check that a find action exists for this pagination.
   *   - dispatch that find function, which in turn reads from this pagination module.
   */
  actions: {
    setTotal({ state, commit, dispatch }, total) {
      commit('setTotal', total);
    },
    setLimit({ state, commit, dispatch }, limit) {
      commit('setLimit', limit);
      dispatchFromParent(state, 'findAction', dispatch);
    },
    setPage({ state, commit, dispatch }, page) {
      commit('setPage', page);
      dispatchFromParent(state, 'findAction', dispatch);
    },
    setSortField({ state, commit, dispatch }, sortField, second) {
      commit('setSortField', sortField);
      dispatchFromParent(state, 'findAction', dispatch);
    },
    setSortDirection({ state, commit, dispatch }, sortDirection) {
      commit('setSortDirection', sortDirection);
      dispatchFromParent(state, 'findAction', dispatch);
    },
  },
};

/**
 * Helper function to generate a new instance of the pagination module and injects/overrides
 * any addtional state values
 *
 * @param {Object} additionalState any additional state to add to/override default values
 * @returns {Object} new instance of the pagination module for parent
 */
export function generatePaginationModule(additionalState) {
  const paginationModuleCopy = { ...paginationModule };
  paginationModuleCopy.state = {
    ...paginationDefaultState,
    ...additionalState,
  };
  return paginationModuleCopy;
}

export default paginationModule;
